import { mapCartItem, useRefetchCartInfo } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { AddToCartMutationApi, useAddToCartMutationApi } from 'graphql/generated';
import { useLatest } from 'hooks/ui/useLatest';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmListNameType } from 'types/gtm';
import { onChangeCartItemGtmEventHandler } from 'utils/Gtm/EventHandlers';
import { useGtmCartEventInfo } from 'utils/Gtm/Gtm';

type AddToCartActionSimple = (
    productUuid: string,
    listIndex: number | null,
    quantity: number,
    gtmListName: GtmListNameType,
    cartUuid: string | null,
    isAbsoluteQuantity?: boolean,
) => Promise<AddToCartMutationApi['AddToCart'] | null>;

export const useAddToCartSimple = (): [AddToCartActionSimple, boolean] => {
    const [{ fetching }, addToCart] = useAddToCartMutationApi();
    const refetchCartInfo = useRefetchCartInfo();

    const { defaultStoreUuid } = usePersistStore((s) => s);
    const { currencyCode, url } = useDomainConfig();
    const gtmCartEventInfo = useGtmCartEventInfo();
    const gtmCart = useLatest(gtmCartEventInfo.cart);

    const addToCartActionSimple = useCallback<AddToCartActionSimple>(
        async (productUuid, listIndex, quantity, gtmListName, cartUuid, isAbsoluteQuantity = false) => {
            const addToCartActionResult = await addToCart({
                input: { cartUuid, productUuid, quantity, isAbsoluteQuantity, productCatnum: null },
                defaultStoreUuid,
                selectedStoreUuid: null,
            });

            const addToCartResult = addToCartActionResult.data?.AddToCart;

            if (addToCartResult === undefined) {
                return null;
            }

            const addedCartItem = addToCartResult.addProductResult.cartItem;

            if (addedCartItem === null) {
                return addToCartResult;
            }

            const quantityDifference = addToCartResult.addProductResult.quantityDifference;
            const absoluteEventValue =
                Number.parseFloat(addedCartItem.product.price.priceWithoutVat) * Math.abs(quantityDifference);
            const absoluteEventValueWithTax =
                Number.parseFloat(addedCartItem.product.price.priceWithVat) * Math.abs(quantityDifference);

            dispatchBroadcastChannel({ name: 'refetchCart' });
            refetchCartInfo();

            setTimeout(() => {
                onChangeCartItemGtmEventHandler(
                    gtmCart.current,
                    mapCartItem(addedCartItem, currencyCode),
                    currencyCode,
                    absoluteEventValue,
                    absoluteEventValueWithTax,
                    listIndex,
                    quantityDifference,
                    gtmListName,
                    url,
                );
            }, 0);

            return addToCartResult;
        },
        [addToCart, defaultStoreUuid, refetchCartInfo, gtmCart, currencyCode, url],
    );

    return [addToCartActionSimple, fetching];
};
